import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, RadioButtonWithLabelWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';

import { AuthFederationService, IAuthFederationKey } from './service/AuthFederationService';

export const WIF_KEYS_LOADING_TEST_ID = 'wif-keys-loading';
export const WIF_KEYS_LOAD_ERROR_TEST_ID = 'wif-keys-load-error';
export const WIF_KEYS_TABLE_TEST_ID = 'wif-keys-table';
export const WIF_KEYS_CREATE_BUTTON_TEST_ID = 'wif-keys-create-button';
export const WIF_KEYS_CREATE_ERROR_TEST_ID = 'wif-keys-create-error';
export const WIF_KEYS_MAX_ERROR_TEST_ID = 'wif-keys-max-error';

export function wifKeyRowTestId(kid: string): string {
  return `wif-key-row-${kid}`;
}
export function wifKeyActiveRadioTestId(kid: string): string {
  return `wif-key-active-radio-${kid}`;
}
export function wifKeyDeleteTestId(kid: string): string {
  return `wif-key-delete-${kid}`;
}

interface WifKeysSectionProps {
  activeKeyId: string | null;
  onActiveKeyIdChange: (newActiveKeyId: string | null) => void;
}

export function WifKeysSection(props: WifKeysSectionProps): JSX.Element {
  const { activeKeyId, onActiveKeyIdChange } = props;

  const [keys, setKeys] = React.useState<IAuthFederationKey[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState(false);
  const [maxKeysError, setMaxKeysError] = React.useState(false);

  React.useEffect(() => {
    AuthFederationService.getKeys()
      .then((k) => setKeys(k))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = () => {
    setCreateError(false);
    setMaxKeysError(false);
    setCreating(true);
    AuthFederationService.createKey()
      .then((newKey) => setKeys((prev) => [...prev, newKey]))
      .catch((error: any) => {
        // Check for max keys error by error code (400983)
        const errorCode = error?.data?.error?.code;
        // Check both string and number forms of the error code
        if (errorCode === '400983' || errorCode === 400983) {
          setMaxKeysError(true);
        } else {
          setCreateError(true);
        }
      })
      .finally(() => setCreating(false));
  };

  const handleActivate = (kid: string) => {
    AuthFederationService.activateKey(kid)
      .then(() => {
        // Server returns 204 with no body, so fetch the updated state
        return AuthFederationService.getAdmin();
      })
      .then((admin) => onActiveKeyIdChange(admin.activeKeyId))
      .catch(() => {});
  };

  const handleDelete = (kid: string) => {
    openReactConfirmationDialog<string, void>({
      message: Messages.authFederationKeyDeleteConfirm({ KID: kid }),
      paramsForAction: kid,
      actionName: Messages.authFederationKeyDelete(),
      isActionDelete: true,
      dialogWidth: TabDialogWidth.width375px,
      action: (kidToDelete) =>
        AuthFederationService.deleteKey(kidToDelete).then(() => {
          setKeys((prev) => prev.filter((k) => k.kid !== kidToDelete));
          setMaxKeysError(false);
        })
    });
  };

  if (loading) {
    return (
      <div style={{ marginTop: '16px' }}>
        <div data-tb-test-id={WIF_KEYS_LOADING_TEST_ID}>{Messages.authFederationKeysLoading()}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ marginTop: '16px' }}>
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId={WIF_KEYS_LOAD_ERROR_TEST_ID}>
          <div>{Messages.authFederationKeysLoadError()}</div>
        </CompactInlineAlertWidget>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{Messages.authFederationKeysTitle()}</div>

      <div style={{ marginBottom: '8px' }}>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          testId={WIF_KEYS_CREATE_BUTTON_TEST_ID}
          type='button'
          disabled={creating}
          onClick={handleCreate}
        >
          {Messages.authFederationKeyCreateButton()}
        </ButtonWidget>
      </div>

      {createError && (
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId={WIF_KEYS_CREATE_ERROR_TEST_ID}>
          <div>{Messages.authFederationKeyCreateError()}</div>
        </CompactInlineAlertWidget>
      )}

      {maxKeysError && (
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId={WIF_KEYS_MAX_ERROR_TEST_ID}>
          <div>{Messages.authFederationKeyMaxError()}</div>
        </CompactInlineAlertWidget>
      )}

      <table data-tb-test-id={WIF_KEYS_TABLE_TEST_ID}>
        <thead>
          <tr>
            <th>{Messages.authFederationKeyId()}</th>
            <th>{Messages.authFederationKeyAlgorithm()}</th>
            <th>{Messages.authFederationKeyIsActive()}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key, index) => {
            const isActive = key.kid === activeKeyId;
            return (
              <tr key={key.kid} data-tb-test-id={wifKeyRowTestId(key.kid)}>
                <td>{key.kid}</td>
                <td>{key.alg}</td>
                <td>
                  <RadioButtonWithLabelWidget
                    groupName='wif-active-key'
                    index={index}
                    value={key.kid}
                    label={null}
                    checked={isActive}
                    handleChange={(kid: string) => handleActivate(kid)}
                    testId={wifKeyActiveRadioTestId(key.kid)}
                  />
                </td>
                <td>
                  <ButtonWidget
                    buttonType={ButtonType.Destructive}
                    testId={wifKeyDeleteTestId(key.kid)}
                    type='button'
                    disabled={isActive}
                    onClick={() => handleDelete(key.kid)}
                  >
                    {Messages.authFederationKeyDelete()}
                  </ButtonWidget>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
